import { MonitorDevice } from "@assets/icons";
import { room_logo } from "@assets/images";
import {
  DeviceStatusType,
  getStatusSxColorFn,
} from "@common/components/ListDetailViews/DetailView/DeviceCard/const";
import { CatalogContext } from "@common/components/CatalogProvider";
import DeviceCardAccordion, {
  DeviceCardAccordionType,
} from "@common/components/ListDetailViews/DetailView/DeviceCard/DeviceCardAccordion";
import DeviceCardFooter from "@common/components/ListDetailViews/DetailView/DeviceCard/DeviceCardFooter";
import DeviceCardHeader from "@common/components/ListDetailViews/DetailView/DeviceCard/DeviceCardHeader";
import DeviceCardImage from "@common/components/ListDetailViews/DetailView/DeviceCard/DeviceCardImage";
import DeviceCardInformations from "@common/components/ListDetailViews/DetailView/DeviceCard/DeviceCardInformations";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { useErrorNotification } from "@common/components/Notification";
import { getModelLogo } from "@common/utils/devices/logo";
import {
  canParticipantInterract,
  isManualConference,
  isMeetingFinished,
  isMeetingInProgress,
  isParticipantConnected,
} from "@common/utils/meetings";
import { canViewDevice } from "@common/utils/user";
import {
  AddIcCall,
  CallEnd,
  CameraIndoor,
  DisplaySettingsRounded,
  EventBusy,
  Mic,
  MicOff,
  Sms,
  StickyNote2,
  Videocam,
  VideocamOff,
  VolumeOff,
  VolumeUp,
} from "@mui/icons-material";
import { Box, Card, styled } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { CallingMethod } from "@services/devices/types";
import {
  inviteParticipant,
  muteParticipant,
  unmuteParticipant,
} from "@services/meetings";
import {
  ChannelDirection,
  ChannelType,
  ConferenceParticipant,
  ConnectionState,
  McuType,
  MeetingSetup,
  MeetingType,
} from "@services/meetings/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import React, {
  useEffect,
  useState,
  useContext,
  PropsWithChildren,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import MessageAll from "../../Monitoring/Drawers/MessageAll";
import { MonitorFrameType } from "..";
import { getBadgeImage, getStatus } from "./const";
import EndCallDialog from "./EndCallDialog";
import ReleaseRoomDialog from "./ReleaseRoomDialog";
import RoomCommentDrawer from "./RoomCommentDrawer";
import { useSimpleReducer } from "@common/hooks";

type ParticipantCardProps = {
  index: number;
  monitor: MonitorFrameType;
  setMonitor: (value: MonitorFrameType) => void;
  previousPage: string;
  participant: ConferenceParticipant;
  handleClickInvite?: (participant?: ConferenceParticipant) => void;
  secondaryCodecs?: ConferenceParticipant[];
};

const StyledLink = styled(Link)({
  textDecoration: "none",
  width: "100%",
  color: "unset",
  display: "flex",
  flexGrow: 1,
});

const CardLink = (props: PropsWithChildren<ParticipantCardProps>) => {
  return props.participant.room ? (
    <StyledLink
      to={
        props.participant.device?.id
          ? `/assets/devices/${props.participant.device?.id}`
          : `/assets/rooms/${props.participant.room?.id}`
      }
      state={{ previous: props.previousPage }}
    >
      {props.children}
    </StyledLink>
  ) : (
    <>{props.children}</>
  );
};

const Container = styled(Box)(({ theme }) => ({
  width: "225px",
  height: "340px",
  "& .MuiPaper-root": {
    height: "100%",
    "&.MuiCard-root": {
      backgroundImage: "unset",
    },
    backgroundColor: theme.palette.background.default,
    borderWidth: "1px",
    borderStyle: "solid",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    alignItems: "center",
    "& .MuiCardContent-root": {
      alignSelf: "start",
    },
  },
}));

const ParticipantCard = (props: ParticipantCardProps) => {
  const {
    index,
    participant,
    handleClickInvite,
    secondaryCodecs = [],
    monitor,
    setMonitor,
  } = props;
  const { room, device, name, address } = participant;
  const dispatchError = useErrorNotification();
  const { t } = useTranslation();
  const catalog = useContext(CatalogContext);
  const rrContext = useContext(RoomsRealtimeContext);
  const [user] = useAuthenticatedUser();
  const { meeting } = { ...rrContext.detail };

  const [
    {
      image,
      openExpandedControls,
      openMessage,
      openEndDialog,
      openReleaseDialog,
      openRoomComment,
      disabledInvite,
    },
    dispatch,
  ] = useSimpleReducer<{
    image: string;
    openExpandedControls: boolean;
    openMessage: boolean;
    openEndDialog: boolean;
    openReleaseDialog: boolean;
    openRoomComment: boolean;
    disabledInvite: boolean;
  }>({
    image: "",
    openExpandedControls: false,
    openMessage: false,
    openEndDialog: false,
    openReleaseDialog: false,
    openRoomComment: false,
    disabledInvite: false,
  });

  const handleInvite = async (participant: ConferenceParticipant) => {
    handleClickInvite && handleClickInvite(participant);
    dispatch({ disabledInvite: true });
    try {
      await inviteParticipant(rrContext.detail?.meeting?.id, participant);
      rrContext.detail?.refetch();
      handleClickInvite && handleClickInvite();
    } catch (e) {
      dispatchError(e);
    }
    dispatch({ disabledInvite: false });
  };

  const handleMute = async (
    type: ChannelType,
    direction: ChannelDirection,
    value: boolean,
    participantId: ConferenceParticipant["id"]
  ) => {
    try {
      if (value === true) {
        await unmuteParticipant(
          rrContext.detail?.meeting?.id,
          participantId,
          type,
          direction
        );
        await rrContext.detail?.refetch();
      } else {
        await muteParticipant(
          rrContext.detail?.meeting?.id,
          participantId,
          type,
          direction
        );
        await rrContext.detail?.refetch();
      }
    } catch (e) {
      dispatchError(e);
    }
  };
  const [status, label] = getStatus(participant, meeting).map((v, i) =>
    i === 1 ? t(v) : v
  ) as [DeviceStatusType, string];

  const getControls = (): {
    permanentControls?: DeviceCardAccordionType["permanentControls"];
    expandedControls?: DeviceCardAccordionType["expandedControls"];
  } => {
    let res: {
      permanentControls: DeviceCardAccordionType["permanentControls"];
      expandedControls: DeviceCardAccordionType["expandedControls"];
    } = {
      permanentControls: [],
      expandedControls: [],
    };

    // Control Panel Url
    if (participant?.room?.details?.controlPanelUrl) {
      res.expandedControls?.push({
        icon: <DisplaySettingsRounded />,
        onClick: () => {
          window.open(
            participant.room?.details?.controlPanelUrl
              ?.toLocaleLowerCase()
              .startsWith("http")
              ? participant.room?.details?.controlPanelUrl
              : `http://${participant.room?.details?.controlPanelUrl}`,
            "_blank"
          );
        },
        tooltip: t("Control Panel URL"),
      });
    }

    // Notes
    if (participant.notes && participant.notes !== "")
      res.expandedControls?.push({
        icon: <StickyNote2 />,
        onClick: () => dispatch({ openRoomComment: true }),
        tooltip: t("Notes"),
      });

    // Room camera
    if (
      participant.room &&
      participant.room.details &&
      participant.room.details.monitoredIpAddress &&
      participant.room.details.monitoredIpAddress !== "" &&
      !meeting?.monitoringOnly
    )
      res.expandedControls?.push({
        icon: <CameraIndoor />,
        onClick: () => {
          window.open(
            participant?.room?.details?.monitoredIpAddress
              ?.toLocaleLowerCase()
              .startsWith("http")
              ? participant?.room?.details?.monitoredIpAddress
              : `http://${participant?.room?.details?.monitoredIpAddress}`,
            "_blank"
          );
        },
        tooltip: t("Room Camera"),
      });
    if (
      isMeetingInProgress(meeting) &&
      participant.address &&
      meeting?.type !== MeetingType.PointToPoint &&
      !isManualConference(meeting) &&
      participant.scheduled &&
      participant.room &&
      !isParticipantConnected(participant) &&
      !participant.released
    )
      res.expandedControls?.push({
        icon: <EventBusy />,
        onClick: () => dispatch({ openReleaseDialog: true }),
        tooltip: t("Release Room"),
      });

    if (participant?.status === ConnectionState.Connected) {
      // conditions should be revisited, in 5.x we were only showing live overview in conference, I think in 6.0 we could only check for apiSupported and canViewDevice
      // Monitor

      if (!meeting?.monitoringOnly) {
        if (
          participant.externalId &&
          !participant.released &&
          isParticipantConnected(participant) &&
          !isMeetingFinished(meeting) &&
          participant?.device?.apiSupported &&
          canViewDevice(user)
        )
          res.expandedControls?.push({
            icon: <MonitorDevice />,
            onClick: () =>
              setMonitor(
                monitor?.index === index ? undefined : { index, participant }
              ),
            tooltip: t("Monitor"),
          });

        // Send Message
        if (canParticipantInterract(participant, meeting)) {
          res.expandedControls?.push({
            icon: <Sms />,
            onClick: () => dispatch({ openMessage: true }),
            tooltip: t("Send Message"),
          });
          if (
            meeting?.mcuType === McuType.RMX &&
            participant.audioMuteRx !== undefined
          )
            res.expandedControls?.push({
              icon: participant.audioMuteRx ? <VolumeOff /> : <VolumeUp />,
              onClick: () =>
                handleMute(
                  ChannelType.Audio,
                  ChannelDirection.In,
                  participant.audioMuteRx ?? true,
                  participant.id
                ),
              tooltip: t(
                `${participant.audioMuteRx ? "UnMute" : "Mute"} Volume`
              ),
            });

          res.expandedControls?.push({
            icon: <CallEnd />,
            onClick: () => dispatch({ openEndDialog: true }),
            color: "red",
            tooltip: t("Disconnect"),
          });

          if (participant.audioMuteTx !== undefined)
            res.permanentControls?.push({
              icon: participant.audioMuteTx ? <MicOff /> : <Mic />,
              onClick: () =>
                handleMute(
                  ChannelType.Audio,
                  ChannelDirection.Out,
                  participant.audioMuteTx ?? true,
                  participant.id
                ),
              tooltip: t(participant.audioMuteTx ? "UnMute" : "Mute"),
            });
          if (
            (meeting?.mcuType === McuType.RMX ||
              meeting?.mcuType === McuType.DMA) &&
            participant.videoMuteTx !== undefined
          )
            res.permanentControls?.push({
              icon: participant.videoMuteTx ? <VideocamOff /> : <Videocam />,
              onClick: () =>
                handleMute(
                  ChannelType.Video,
                  ChannelDirection.Out,
                  participant.videoMuteTx ?? true,
                  participant.id
                ),
              tooltip: t(
                `${participant.videoMuteTx ? "UnMute" : "Mute"} Camera`
              ),
            });
        }
      }
    } else {
      if (
        isMeetingInProgress(meeting) &&
        participant.address &&
        meeting?.type !== MeetingType.PointToPoint &&
        !(
          meeting?.scheduledMeeting &&
          meeting.scheduledMeeting.meetingSetup === MeetingSetup.Manual
        ) &&
        !(
          participant.device &&
          participant.device.callingMethod === CallingMethod.None
        )
      )
        res.permanentControls?.push({
          icon: <AddIcCall />,
          onClick: () => handleInvite(participant),
          tooltip: t("Invite"),
          disabled:
            disabledInvite ??
            secondaryCodecs?.some((codecs) => participant.id === codecs.id),
        });
    }
    return res;
  };

  useEffect(() => {
    if (device)
      dispatch({
        image: getModelLogo(
          __symphony_env__.REACT_APP_SERVER_URL,
          device,
          catalog
        ),
      });
    else if (room?.logo)
      dispatch({ image: __symphony_env__.REACT_APP_SERVER_URL + room.logo });
    else dispatch({ image: room_logo });
  }, [device, room]);

  return (
    <Container>
      <EndCallDialog
        participant={participant}
        open={openEndDialog}
        onClose={() => dispatch({ openEndDialog: false })}
      />
      <ReleaseRoomDialog
        participant={participant}
        open={openReleaseDialog}
        onClose={() => dispatch({ openReleaseDialog: false })}
      />
      <RoomCommentDrawer
        comment={participant.notes ?? ""}
        open={openRoomComment}
        onClose={() => dispatch({ openRoomComment: false })}
      />
      <MessageAll
        open={openMessage}
        onClose={() => dispatch({ openMessage: false })}
      />
      <Card sx={{ borderColor: getStatusSxColorFn(status) }}>
        {openExpandedControls ? (
          <Box sx={{ maxWidth: "100%" }}>
            <CardLink {...props}>
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={{ width: '100%', pl: "1rem",
                  alignItems: "center", }}
              >
                <DeviceCardImage
                  image={image}
                  {...getBadgeImage(participant, meeting)}
                />
                <Grid sx={{ overflow: "hidden" }}>
                  <DeviceCardInformations
                    name={room ? room?.name : name}
                    label1={room ? room?.location?.name : address}
                    label2={device?.name}
                  />
                </Grid>
              </Grid>
            </CardLink>
          </Box>
        ) : (
          <Grid
            sx={{
              flexGrow: "1",
              width: "100%",
            }}
          >
            <Grid
              container
              direction="column"
              sx={{ width: '100%', alignItems: "center", }}
            >
              <DeviceCardHeader
                device={device}
                deviceId={device?.id}
                hideStatuses={true}
                previousPage={String(meeting?.meetingId)}
              />
              <CardLink {...props}>
                <Grid
                  container
                  direction="column"
                  sx={{ width: '100%', alignItems: "center", }}
                >
                  <DeviceCardImage
                    image={image}
                    {...getBadgeImage(participant, meeting)}
                  />
                  <DeviceCardInformations
                    name={room ? room?.name : name}
                    label1={room ? room?.location?.name : address}
                    label2={device?.name}
                  />
                </Grid>
              </CardLink>
            </Grid>
          </Grid>
        )}

        <DeviceCardAccordion
          open={openExpandedControls}
          onClick={() =>
            dispatch({ openExpandedControls: !openExpandedControls })
          }
          {...getControls()}
        />
        <DeviceCardFooter
          label={label}
          status={status}
          tooltip={participant?.statusDetails}
        />
      </Card>
    </Container>
  );
};

export default ParticipantCard;
